import {
  type AddGuestRequest,
  isTest,
  type Patrol,
  type Person,
} from '@local/common-lib';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { Theme } from '@mui/material';
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import type { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { Fragment, useMemo } from 'react';
import { ConfirmButton } from '../../components/confirm-button/index.js';
import { RelativeDate } from '../../components/date/index.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import { PatrolTimeline } from '../../components/patrol-timeline/index.js';
import { PatrolTimeTable } from '../../components/patrol-timeline/patrol-time-table.js';
import { useFetchAllCache } from '../../hooks/use-cache.js';
import { usePromise } from '../../hooks/use-promise.js';
import type { AdminCompletePatrolData } from './admin-complete.js';
import { AdminCompletePatrolDialog } from './admin-complete.js';
import { AssignmentResponseAlert } from './assignment-response-alert.js';
import { GuestAssignmentRow } from './guest-assignment-row.js';
import { PatrollerAssignmentRow } from './patrol-assignment-row.js';

const StyledTable = styled(Table)(({ theme }) => ({
  '& tbody td': {
    border: 'none',
  },
  '& tbody tr': {
    borderTopColor: (theme as Theme).palette.divider,
    borderTopStyle: 'solid',
    borderTopWidth: 1,
  },
}));

interface ViewProps<T> {
  addGuestForm: FormikProps<AddGuestRequest>;
  addPatrollerForm: FormikProps<AddPatrollerData>;
  adminCompleteForm: FormikProps<AdminCompletePatrolData>;
  busy: boolean;
  data: T;
  handleAcceptPatroller: (patrollerId: string) => void;
  handleDeclinePatroller: (patrollerId: string) => void;
  handleMyAssignmentAccept: () => void;
  handleMyAssignmentDecline: () => void;
  handleRemoveGuest: (guestId: string) => void;
  handleRemovePatroller: (patrollerId: string) => void;
  handleSetPrimaryPatroller: (patrollerId: string) => void;
  handleStartEccWarningClose: () => void;
  handleStartEccWarningContinue: () => void;
  id: string;
  isAdmin: boolean;
  isMine: boolean;
  myAssignmentPending: boolean;
  onCancel: (didNotShow: boolean) => unknown;
  onEccLogOn: () => unknown;
  onEdit: () => unknown;
  onFinalize: () => unknown;
  onRecord: () => void;
  onShowAddGuest: () => void;
  onShowAddPatroller: () => void;
  onShowAdminComplete: () => void;
  onStart: () => void;
  setShowTimeTable: (show: boolean) => void;
  showAddGuest: boolean;
  showAddPatroller: boolean;
  showAdminComplete: boolean;
  showTimeTable: boolean;
  startEccWarningOpen: boolean;
}

export interface AddPatrollerData {
  patroller: Person.View | null;
}

const AddButton: FC = () => (
  <Button
    fullWidth
    sx={{
      borderRadius: 100,
    }}
    type="submit"
    variant="contained"
  >
    Add
  </Button>
);

const CancelButton: FC = () => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  if (!smallScreen) {
    return (
      <IconButton type="reset">
        <CloseIcon />
      </IconButton>
    );
  }

  return (
    <Button
      fullWidth
      sx={{
        borderRadius: 100,
      }}
      type="reset"
      variant="outlined"
    >
      Cancel
    </Button>
  );
};

const sortBy = (primaryPatroller?: string) => (a: string, b: string) => {
  if (a === primaryPatroller) return -1;
  if (b === primaryPatroller) return 1;
  return a.localeCompare(b);
};

export const DisplayPatrolView: FC<ViewProps<Patrol.Type>> = ({
  addGuestForm,
  addPatrollerForm,
  adminCompleteForm,
  busy,
  data,
  handleAcceptPatroller,
  handleDeclinePatroller,
  handleMyAssignmentAccept,
  handleMyAssignmentDecline,
  handleRemoveGuest,
  handleRemovePatroller,
  handleSetPrimaryPatroller,
  handleStartEccWarningClose,
  handleStartEccWarningContinue,
  isAdmin,
  isMine,
  myAssignmentPending,
  onCancel,
  onEccLogOn,
  onEdit,
  onFinalize,
  onRecord,
  onShowAddGuest,
  onShowAddPatroller,
  onShowAdminComplete,
  onStart,
  setShowTimeTable,
  showAddGuest,
  showAddPatroller,
  showAdminComplete,
  showTimeTable,
  startEccWarningOpen,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const personCache = useFetchAllCache('person');
  const [allPersons, , state] = usePromise(
    () => personCache.all<Person.View>(),
    [personCache],
  );
  const persons = useMemo(() => {
    const filtered =
      allPersons?.filter(
        (person) => !person.finished && person.roles.includes('patroller'),
      ) ?? [];
    filtered.sort((a: Person.View, b: Person.View) =>
      a.lastName.localeCompare(b.lastName),
    );
    return filtered;
  }, [allPersons]);

  const start = DateTime.fromJSDate(data.when);
  const finish = start.plus({ hours: data.hours });
  const now = DateTime.now();
  const isPatrol = !data.type || data.type === 'patrol';
  const futurePatrol = now < start;
  const canStart = now >= start.minus({ minutes: 60 }) && now < finish;
  // patrollers on the patrol should record it themselves; only offer the
  // administrative completion to them well after the patrol should have finished
  const canAdminComplete =
    !data.finished && now >= (isMine ? finish.plus({ hours: 3 }) : finish);
  const canEccLogOn = now >= start.minus({ minutes: 90 }) && now < finish;
  const patrollers = data.patrollers ?? {};
  const guests = data.guests ?? {};
  const actionButtonSx = smallScreen
    ? {
        flex: '1 1 10rem',
        minWidth: 0,
        whiteSpace: 'nowrap',
      }
    : {
        whiteSpace: 'nowrap',
      };

  return (
    <CenterLayout>
      <Stack direction="column" spacing={3}>
        {myAssignmentPending &&
          data.finalized &&
          !data.cancelled &&
          !data.started &&
          now < finish && (
            <AssignmentResponseAlert
              busy={busy}
              onAccept={handleMyAssignmentAccept}
              onDecline={handleMyAssignmentDecline}
            />
          )}
        <Card>
          {isTest(data) ? (
            <CardHeader
              subheader="Emails will only be sent to patrollers and guests, not watchers or ECC."
              subheaderTypographyProps={{
                color: (theme: Theme) => theme.palette.error.contrastText,
              }}
              sx={{
                backgroundColor: (theme: Theme) => theme.palette.error.main,
                color: (theme: Theme) => theme.palette.error.contrastText,
                p: 1.5,
                textAlign: 'center',
              }}
              title={`TEST ${data.type?.toUpperCase() ?? 'PATROL'}`}
            />
          ) : undefined}
          {data.cancelled ? (
            <CardHeader
              sx={{
                backgroundColor: (theme: Theme) => theme.palette.info.main,
                color: (theme: Theme) => theme.palette.info.contrastText,
                p: 1.5,
                textAlign: 'center',
              }}
              title="CANCELLED"
            />
          ) : (
            !data.finalized && (
              <CardHeader
                sx={{
                  backgroundColor: (theme: Theme) => theme.palette.warning.main,
                  color: (theme: Theme) => theme.palette.warning.contrastText,
                  p: 1.5,
                  textAlign: 'center',
                }}
                // subheader="To notify assigned patrollers, this patrol needs to be finalized."
                title="DRAFT"
              />
            )
          )}
          <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant={smallScreen ? 'h6' : 'h4'}>
              {data.name ? `${data.name} - ` : undefined}
              {DateTime.fromJSDate(data.when).toLocaleString({
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                month: 'short',
                weekday: 'short',
              })}
            </Typography>
            <Stack
              direction="row"
              sx={{
                alignSelf: smallScreen ? 'stretch' : 'end',
                flexWrap: smallScreen ? 'wrap' : 'nowrap',
                gap: 1.5,
              }}
              useFlexGap
            >
              {isAdmin && !data.started && !data.cancelled && !futurePatrol && (
                <ConfirmButton
                  busy={busy}
                  onClick={() => onCancel(true)}
                  sx={actionButtonSx}
                  variant="contained"
                >
                  Did Not Show
                </ConfirmButton>
              )}
              {isAdmin && !data.started && !data.cancelled && (
                <ConfirmButton
                  busy={busy}
                  onClick={() => onCancel(false)}
                  sx={actionButtonSx}
                  variant="contained"
                >
                  Cancel
                </ConfirmButton>
              )}
              {isAdmin && !data.started && !data.cancelled && futurePatrol && (
                <LoadingButton
                  loading={busy}
                  onClick={() => onEdit()}
                  sx={actionButtonSx}
                  variant="contained"
                >
                  Edit
                </LoadingButton>
              )}
              {isAdmin && !data.finalized && !data.cancelled && (
                <LoadingButton
                  loading={busy}
                  onClick={() => onFinalize()}
                  sx={actionButtonSx}
                  variant="contained"
                >
                  Finalize
                </LoadingButton>
              )}
              {isMine &&
                data.finalized &&
                !data.eccLogOnNotification &&
                !data.started &&
                isPatrol &&
                canEccLogOn && (
                  <LoadingButton
                    loading={busy}
                    onClick={onEccLogOn}
                    startIcon={<EmailIcon />}
                    sx={actionButtonSx}
                    variant="contained"
                  >
                    ECC LogOn
                  </LoadingButton>
                )}
              {isMine &&
                data.finalized &&
                !data.started &&
                isPatrol &&
                canStart && (
                  <Fragment>
                    <LoadingButton
                      loading={busy}
                      onClick={onStart}
                      startIcon={<StartIcon />}
                      sx={actionButtonSx}
                      variant="contained"
                    >
                      Start
                    </LoadingButton>
                    <Dialog
                      aria-describedby="alert-dialog-description"
                      aria-labelledby="alert-dialog-title"
                      maxWidth="sm"
                      onClose={handleStartEccWarningClose}
                      open={startEccWarningOpen}
                    >
                      <DialogTitle id="alert-dialog-title">
                        {!data.eccLogOnNotification
                          ? 'ECC LogOn Not Sent'
                          : 'ECC Response Not Received'}
                      </DialogTitle>
                      <DialogContent>
                        {!data.eccLogOnNotification ? (
                          <DialogContentText
                            component="div"
                            id="alert-dialog-description"
                          >
                            <p>
                              ECC LogOn has not yet been sent for this patrol.
                            </p>
                            <p>
                              This can be sent now, but note that it can take up
                              to 30 minutes to process before you will receive
                              your Police Event Number and you can start your
                              patrol.
                            </p>
                            <p>
                              Alteratively you can start now, but you will must
                              phone ECC to get your Event number and manually
                              enter it on the start details page.
                            </p>
                          </DialogContentText>
                        ) : (
                          <DialogContentText id="alert-dialog-description">
                            <p>
                              ECC LogOn has been send for this patrol{' '}
                              <RelativeDate
                                date={DateTime.fromJSDate(
                                  data.eccLogOnNotification.when,
                                )}
                              />
                              . However, a response has not yet received.
                            </p>
                            <p>
                              The expected processing time for ECC LogOn is up
                              to 30 minutes.
                            </p>
                            <p>
                              If you choose to start now you will have to phone
                              ECC and manually enter the Event Number into the
                              start details page.
                            </p>
                          </DialogContentText>
                        )}
                      </DialogContent>
                      <DialogActions>
                        {!data.eccLogOnNotification ? (
                          <Fragment>
                            <Button onClick={handleStartEccWarningClose}>
                              Go Back
                            </Button>
                            <Button onClick={onEccLogOn}>Send ECC LogOn</Button>
                          </Fragment>
                        ) : (
                          <Button onClick={handleStartEccWarningClose}>
                            Wait
                          </Button>
                        )}
                        <Button
                          autoFocus
                          onClick={handleStartEccWarningContinue}
                        >
                          Start Anyway
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Fragment>
                )}
              {isMine &&
                data.finalized &&
                data.started &&
                isPatrol &&
                !data.finished && (
                  <LoadingButton
                    loading={busy}
                    onClick={onRecord}
                    sx={actionButtonSx}
                    variant="contained"
                  >
                    Continue Recording
                  </LoadingButton>
                )}
              {isAdmin &&
                data.finalized &&
                !data.cancelled &&
                canAdminComplete && (
                  <Fragment>
                    <LoadingButton
                      loading={busy}
                      onClick={onShowAdminComplete}
                      sx={actionButtonSx}
                      variant="contained"
                    >
                      Completed (Admin)
                    </LoadingButton>
                    <AdminCompletePatrolDialog
                      formik={adminCompleteForm}
                      onClose={() => adminCompleteForm.handleReset(undefined)}
                      open={showAdminComplete}
                      patrol={data}
                      persons={allPersons ?? []}
                    />
                  </Fragment>
                )}
            </Stack>
          </CardContent>
        </Card>
        {data.started ? (
          <Card>
            <CardHeader
              action={
                <FormControlLabel
                  control={
                    <Switch
                      checked={showTimeTable}
                      onChange={() => setShowTimeTable(!showTimeTable)}
                    />
                  }
                  label="Details"
                />
              }
              title="Timeline"
            />
            {showTimeTable ? (
              <PatrolTimeTable patrol={data} />
            ) : (
              <CardContent>
                <PatrolTimeline patrol={data} />
              </CardContent>
            )}
          </Card>
        ) : data.eccLogOnNotification ? (
          <Card>
            <CardContent>
              {data.eccLogOnResponse ? (
                <Alert color="success">
                  ECC LogOn notification sent. Response received{' '}
                  <RelativeDate
                    date={DateTime.fromJSDate(data.eccLogOnResponse.when)}
                  />
                  .
                </Alert>
              ) : (
                <Alert color="warning">
                  ECC LogOn notification sent{' '}
                  <RelativeDate
                    date={DateTime.fromJSDate(data.eccLogOnNotification.when)}
                  />
                  , response pending.
                </Alert>
              )}
            </CardContent>
          </Card>
        ) : undefined}
        {/* Patrollers */}
        <Card>
          <CardHeader title="Patrollers" />
          {Object.keys(patrollers).length ? (
            <StyledTable aria-label="assigned patrollers">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  {!smallScreen && <TableCell>Phone</TableCell>}
                  {!smallScreen && <TableCell>Email</TableCell>}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(patrollers)
                  .sort(sortBy(data.primaryPatroller))
                  .map((patrollerId) => (
                    <PatrollerAssignmentRow
                      assignment={patrollers[patrollerId]}
                      futurePatrol={futurePatrol}
                      handleAccept={handleAcceptPatroller}
                      handleDecline={handleDeclinePatroller}
                      handleRemove={handleRemovePatroller}
                      handleSetPrimary={handleSetPrimaryPatroller}
                      isAdmin={isAdmin}
                      isPrimaryPatroller={data.primaryPatroller === patrollerId}
                      key={patrollerId}
                      patrollerId={patrollerId}
                      persons={personCache}
                    />
                  ))}
              </TableBody>
            </StyledTable>
          ) : (
            <CardContent sx={{ mb: 2 }}>
              <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                No patrollers assigned
              </Typography>
            </CardContent>
          )}
          {isAdmin && futurePatrol && !data.cancelled && !data.started && (
            <CardActions sx={{ borderTop: '1px solid rgba(81, 81, 81, 1)' }}>
              {!showAddPatroller ? (
                <Button
                  onClick={() => onShowAddPatroller()}
                  startIcon={<AddIcon />}
                  variant="outlined"
                >
                  Add Patroller
                </Button>
              ) : (
                <form
                  onReset={addPatrollerForm.handleReset}
                  onSubmit={addPatrollerForm.handleSubmit}
                  style={{ width: '100%' }}
                >
                  <Grid container spacing={3} sx={{ alignItems: 'start' }}>
                    <Grid item md={9} xs={12}>
                      <Autocomplete<Person.View>
                        autoComplete
                        autoHighlight
                        clearOnEscape={true}
                        fullWidth
                        getOptionLabel={(patroller) =>
                          patroller
                            ? `${patroller?.firstName} ${patroller?.lastName?.toUpperCase()}`
                            : ''
                        }
                        id="patroller"
                        loading={state === 'pending'}
                        noOptionsText="No-one found"
                        onChange={(_, selected) =>
                          addPatrollerForm.setFieldValue('patroller', selected)
                        }
                        options={persons}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            autoFocus
                            error={
                              state === 'rejected' ||
                              (addPatrollerForm.touched.patroller &&
                                Boolean(addPatrollerForm.errors.patroller))
                            }
                            helperText={
                              addPatrollerForm.touched.patroller &&
                              addPatrollerForm.errors.patroller
                            }
                            label="Patroller"
                          />
                        )}
                        size="small"
                        value={addPatrollerForm.values.patroller}
                      />
                    </Grid>
                    <Grid item md={2} sx={{ display: 'flex' }} xs={12}>
                      <AddButton />
                    </Grid>
                    <Grid item md={1} sx={{ display: 'flex' }} xs={12}>
                      <CancelButton />
                    </Grid>
                  </Grid>
                </form>
              )}
            </CardActions>
          )}
        </Card>
        {/* Guests */}
        <Card>
          <CardHeader title="Guests" />
          {Object.keys(guests).length ? (
            <StyledTable aria-label="assigned guests">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  {!smallScreen && <TableCell>Email</TableCell>}
                  {futurePatrol && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(guests).map((guestId) => (
                  <GuestAssignmentRow
                    assignment={guests[guestId]}
                    futurePatrol={futurePatrol}
                    guestId={guestId}
                    handleRemove={handleRemoveGuest}
                    isAdmin={isAdmin}
                    key={guestId}
                  />
                ))}
              </TableBody>
            </StyledTable>
          ) : (
            <CardContent sx={{ mb: 2 }}>
              <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                No guests assigned
              </Typography>
            </CardContent>
          )}
          {isAdmin && futurePatrol && !data.cancelled && !data.started && (
            <CardActions sx={{ borderTop: '1px solid rgba(81, 81, 81, 1)' }}>
              {!showAddGuest ? (
                <Button
                  onClick={() => onShowAddGuest()}
                  startIcon={<AddIcon />}
                  variant="outlined"
                >
                  Add Guest
                </Button>
              ) : (
                <form
                  onReset={addGuestForm.handleReset}
                  onSubmit={addGuestForm.handleSubmit}
                  style={{ width: '100%' }}
                >
                  <Grid container spacing={3} sx={{ alignItems: 'start' }}>
                    <Grid item md={3} xs={12}>
                      <TextField
                        autoComplete="off"
                        autoFocus
                        error={Boolean(addGuestForm.errors.name)}
                        fullWidth
                        helperText={addGuestForm.errors.name}
                        id="name"
                        label="Name"
                        name="name"
                        onChange={addGuestForm.handleChange}
                        required
                        size="small"
                        type="text"
                        value={addGuestForm.values.name}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        autoComplete="off"
                        error={Boolean(addGuestForm.errors.phone)}
                        fullWidth
                        helperText={addGuestForm.errors.phone}
                        id="phone"
                        label="Phone number"
                        name="phone"
                        onChange={addGuestForm.handleChange}
                        size="small"
                        type="tel"
                        value={addGuestForm.values.phone}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        autoComplete="off"
                        error={Boolean(addGuestForm.errors.email)}
                        fullWidth
                        helperText={addGuestForm.errors.email}
                        id="email"
                        label="Email address"
                        name="email"
                        onChange={addGuestForm.handleChange}
                        size="small"
                        type="email"
                        value={addGuestForm.values.email}
                      />
                    </Grid>
                    <Grid item md={2} sx={{ display: 'flex' }} xs={12}>
                      <AddButton />
                    </Grid>
                    <Grid item md={1} sx={{ display: 'flex' }} xs={12}>
                      <CancelButton />
                    </Grid>
                  </Grid>
                </form>
              )}
            </CardActions>
          )}
        </Card>
      </Stack>
    </CenterLayout>
  );
};
